import React, { useRef, useCallback } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import Dropzone from 'react-dropzone';
import { OCR_CONSTANTS } from './constants';
import { validateFile, processImageFile } from './utils';
import { showMessage } from '../../../helpers/MessageUtility';

interface OcrDropZoneProps {
  maxFileSizeInMB: number;
  isProcessing: boolean;
  onImageSelected: (imageSrc: string, file: File) => void;
}

export default function OcrDropZone({ maxFileSizeInMB, isProcessing, onImageSelected }: OcrDropZoneProps) {
  const dropzoneRef = useRef<{ open: () => void }>(null);
  const maxFileSizeInBytes = maxFileSizeInMB * 1024 * 1024;

  const openFilePicker = useCallback(() => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  }, []);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: File[]) => {
      if (rejectedFiles.length > 0) {
        const errorMessage = validateFile(rejectedFiles[0], maxFileSizeInMB);
        if (errorMessage) {
          showMessage('error', errorMessage);
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        processImageFile(file)
          .then((result) => {
            onImageSelected(result, file);
          })
          .catch(() => {
            showMessage('error', 'Failed to process image file.');
          });
      }
    },
    [maxFileSizeInMB, onImageSelected]
  );

  return (
    <div className="ocr-image-picker-dropzone-container">
      <Dropzone
        disableClick
        maxSize={maxFileSizeInBytes}
        multiple={false}
        disabled={isProcessing}
        accept={OCR_CONSTANTS.ACCEPTED_FORMATS}
        ref={(r) => {
          dropzoneRef.current = r;
        }}
        onDropAccepted={(files) => onDrop(files, [])}
        onDropRejected={(files) => onDrop([], files)}
      >
        <div className="ocr-upload-text">
          <p>Select an image</p>
          <Icon name="cloud upload" size="huge" />
          <p>Drag and drop {OCR_CONSTANTS.FILE_EXTENSIONS.join(', ')}</p>
        </div>
        <Button className="ocr-browse-button" onClick={openFilePicker} loading={isProcessing} disabled={isProcessing}>
          Browse
        </Button>
      </Dropzone>
    </div>
  );
}
